<template>
  <div class="fmt-tool">
    <div class="fmt-toolbar">
      <span class="fmt-label">{{ $t('tool.Calculator.title') }}</span>
      <span class="fmt-spacer"/>
      <el-button size="small" plain icon="el-icon-document-copy" @click="doCopy">{{ $t('tool.Calculator.copyResult') }}</el-button>
      <el-button size="small" plain icon="el-icon-delete" @click="clearAll">{{ $t("common.action.clear") }}</el-button>
    </div>

    <!-- 显示屏 -->
    <div class="cc-display">
      <div class="cc-expr" v-html="displayExpr"></div>
      <div class="cc-result" v-if="result!==''&&result!==undefined">= {{ result }}</div>
      <div class="cc-error" v-if="errorMsg">{{ errorMsg }}</div>
    </div>

    <!-- 按键区 -->
    <div class="cc-keypad">
      <div class="cc-row">
        <button class="cc-key cc-fn" @click="append('√')">√</button>
        <button class="cc-key cc-fn" @click="append('^')">xʸ</button>
        <button class="cc-key cc-fn" @click="appendAbs">|x|</button>
        <button class="cc-key cc-op" @click="clearAll">C</button>
        <button class="cc-key cc-op" @click="backspace">⌫</button>
      </div>
      <div class="cc-row">
        <button class="cc-key cc-paren" @click="append('(')">(</button>
        <button class="cc-key cc-paren" @click="append(')')">)</button>
        <button class="cc-key cc-fn" @click="append('%')">%</button>
        <button class="cc-key cc-op" @click="append('/')">÷</button>
        <button class="cc-key cc-op" @click="append('*')">×</button>
      </div>
      <div class="cc-row">
        <button class="cc-key cc-num" @click="append('7')">7</button>
        <button class="cc-key cc-num" @click="append('8')">8</button>
        <button class="cc-key cc-num" @click="append('9')">9</button>
        <button class="cc-key cc-op" @click="append('-')">−</button>
      </div>
      <div class="cc-row">
        <button class="cc-key cc-num" @click="append('4')">4</button>
        <button class="cc-key cc-num" @click="append('5')">5</button>
        <button class="cc-key cc-num" @click="append('6')">6</button>
        <button class="cc-key cc-op" @click="append('+')">+</button>
      </div>
      <div class="cc-row">
        <button class="cc-key cc-num" @click="append('1')">1</button>
        <button class="cc-key cc-num" @click="append('2')">2</button>
        <button class="cc-key cc-num" @click="append('3')">3</button>
        <button class="cc-key cc-num" @click="append('0')" style="flex:2.3">0</button>
        <button class="cc-key cc-num" @click="append('.')">.</button>
      </div>
      <div class="cc-row">
        <button class="cc-key cc-eq" @click="calc" style="flex:1">=</button>
      </div>
    </div>

    <div class="fmt-status-bar">
      <span class="fmt-hint">{{ $t('tool.Calculator.hint') }}</span>
    </div>
  </div>
</template>

<script>
export default {
  name: 'Calculator',
  props: { config: { type: Object, default: () => ({}) } },
  data: function(){
    return { expression: '', result: '', errorMsg: '', history: [], histIdx: -1 }
  },
  mounted: function(){
    var self = this
    document.addEventListener('keydown', function(e){
      if (e.key === 'Enter') { e.preventDefault(); self.calc() }
    })
  },
  computed: {
    displayExpr: function(){
      var s = this.expression || '0'
      s = s.replace(/\*/g,'×').replace(/\//g,'÷')
      s = s.replace(/&/g,'&amp;')
      // ^ 后面的数字变上标
      s = s.replace(/\^(\d+(\.\d+)?)/g,'<sup>$1</sup>')
      // √N 或 √(...) → 带上划线
      s = s.replace(/√(\d+(\.\d+)?)/g,'<span class="cc-sqrt"><span class="cc-math-fn">√</span><span class="cc-sqrt-inner">$1</span></span>')
      s = s.replace(/√\(([^)]+)\)/g,'<span class="cc-sqrt"><span class="cc-math-fn">√</span><span class="cc-sqrt-inner">$1</span></span>')
      // |...| → 绝对值
      s = s.replace(/\|([^|]+)\|/g,'<span class="cc-math-fn">|</span>$1<span class="cc-math-fn">|</span>')
      return s
    }
  },
  methods: {
    appendAbs: function(){
      var pipes = (this.expression.match(/\|/g)||[]).length
      if (pipes % 2 === 0) { this.expression += '|' }
      else { this.expression += '|'; this.liveCalc() }
    },
    append: function(s, close){
      this.errorMsg = ''
      this.expression += s
      if (close) this.expression += close
      // 如果按键是完整表达式（如回车），自动算
      if (s === ')' || /\d$/.test(s)) { this.liveCalc() }
    },
    backspace: function(){
      this.expression = this.expression.slice(0, -1)
      this.liveCalc()
    },
    _toEval: function(s){
      return s.replace(/×/g,'*').replace(/÷/g,'/').replace(/−/g,'-')
        .replace(/\^/g,'**')
        .replace(/√(\d+(\.\d+)?)/g,'Math.sqrt($1)')
        .replace(/√\(([^)]+)\)/g,'Math.sqrt($1)')
        .replace(/\|([^|]+)\|/g,'Math.abs($1)')
    },
    liveCalc: function(){
      this.errorMsg = ''
      if (!this.expression.trim()) { this.result = ''; return }
      try {
        var expr = this._toEval(this.expression)
        var val = Function('"use strict"; return (' + expr + ')')()
        this.result = Number.isFinite(val) ? String(val) : 'Error'
      } catch (e) { this.result = '' }
    },
    calc: function(){
      this.errorMsg = ''
      if (!this.expression.trim()) return
      try {
        var expr = this._toEval(this.expression)
        var val = Function('"use strict"; return (' + expr + ')')()
        if (!Number.isFinite(val)) { this.errorMsg = this.$t('tool.Calculator.invalidResult'); return }
        this.result = String(val)
        this.history.push(this.expression)
        this.histIdx = -1
      } catch (e) { this.errorMsg = this.$t('tool.Calculator.exprError') }
    },
    historyUp: function(){
      if (!this.history.length) return
      if (this.histIdx < 0) this.histIdx = this.history.length - 1
      else if (this.histIdx > 0) this.histIdx--
      else return
      this.expression = this.history[this.histIdx]
    },
    historyDown: function(){
      if (this.histIdx < 0 || this.histIdx >= this.history.length - 1) {
        this.histIdx = -1; this.expression = ''; this.result = ''
        return
      }
      this.histIdx++
      this.expression = this.history[this.histIdx]
    },
    doCopy: function(){
      if (!this.result) { this.$message.warning(this.$t('tool.Calculator.noResult')); return }
      navigator.clipboard.writeText(this.result); this.$message.success(this.$t("common.status.copied"))
    },
    clearAll: function(){ this.expression = ''; this.result = ''; this.errorMsg = ''; this.histIdx = -1 },
    doAction: function(a){
      if (a === 'copy') this.doCopy()
      if (a === 'clear') this.clearAll()
    }
  }
}
</script>

<style scoped>
.fmt-tool{display:flex;flex-direction:column;gap:12px;max-width:420px}

.fmt-toolbar{display:flex;align-items:center;gap:8px;flex-wrap:wrap}
.fmt-toolbar >>> .el-button + .el-button{margin-left:0}
.fmt-label{font-size:13px;font-weight:700;color:#1a1f36}
.fmt-spacer{flex:1}

/* 显示屏 */
.cc-display{background:#1a1f36;border-radius:14px;padding:20px 24px;min-height:80px}
.cc-expr{font-size:22px;font-family:'SF Mono',Menlo,Consolas,monospace;color:rgba(255,255,255,.6);word-break:break-all;line-height:1.6;min-height:30px}
.cc-expr >>> sup{font-size:.7em;color:#4ef08e;vertical-align:super}
.cc-expr >>> .cc-math-fn{color:#e6a23c}
.cc-expr >>> .cc-math-op{color:rgba(255,255,255,.4)}
.cc-sqrt{display:inline-flex;align-items:flex-start;white-space:nowrap;vertical-align:middle}
.cc-sqrt .cc-math-fn{font-size:1.3em;line-height:1;margin-top:1px}
.cc-sqrt-inner{display:inline-block;border-top:2px solid #e6a23c;padding:3px 4px 0 2px;margin-top:4px}
.cc-result{font-size:32px;font-weight:700;font-family:'SF Mono',Menlo,Consolas,monospace;color:#4ef08e;margin-top:6px;word-break:break-all}
.cc-error{font-size:14px;color:#f56c6c;margin-top:4px}

/* 按键区 */
.cc-keypad{display:flex;flex-direction:column;gap:6px}
.cc-row{display:flex;gap:6px}
.cc-key{flex:1;padding:14px 0;border:none;border-radius:10px;font-size:18px;font-weight:600;cursor:pointer;transition:.12s;outline:none;font-family:'SF Mono',Menlo,Consolas,monospace}
.cc-key:active{transform:scale(.95)}
.cc-num{background:#f0f2f5;color:#1a1f36}
.cc-num:hover{background:#e4e8f0}
.cc-op{background:#e8f0fe;color:#4e7cff}
.cc-op:hover{background:#d0e0ff}
.cc-op:disabled{opacity:.3;cursor:not-allowed}
.cc-fn{background:#fef9f0;color:#e6a23c;font-size:14px}
.cc-fn:hover{background:#fdf3e0}
.cc-paren{background:#f5f0ff;color:#6b3cf0}
.cc-paren:hover{background:#ede4ff}
.cc-eq{background:linear-gradient(135deg,#4e7cff,#3b6ae8);color:#fff;font-size:24px}
.cc-eq:hover{background:linear-gradient(135deg,#3b6ae8,#2d5ad6)}

.fmt-status-bar{font-size:12px}
.fmt-hint{color:#c0c4cc}
</style>
